import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";
import { createTogglePatterns, createThemePatterns, createColorPatterns } from "./ControlPatternMixins";

const VALID_THEMES = ['ios', 'material', 'neon', 'minimal', 'pill'] as const;
type ToggleTheme = typeof VALID_THEMES[number];

const THEME_ALIASES: Record<string, string> = {
  'apple': 'ios',
  'google': 'material',
  'android': 'material',
  'glow': 'neon',
  'simple': 'minimal',
  'clean': 'minimal',
};

export class ToggleSwitchCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Mixin patterns: toggle, theme, colors
    for (const [regex, handler] of createTogglePatterns('isOn')) {
      this.commandPatterns.set(regex, handler);
    }
    for (const [regex, handler] of createThemePatterns([...VALID_THEMES], THEME_ALIASES)) {
      this.commandPatterns.set(regex, handler);
    }
    for (const [regex, handler] of createColorPatterns(['onColor', 'offColor', 'thumbColor', 'borderColor', 'trackBorderColor'])) {
      this.commandPatterns.set(regex, handler);
    }

    // Size: "size large", "make it small"
    this.commandPatterns.set(
      /(?:set )?size (?:to )?(sm|small|md|medium|lg|large)/i,
      (match) => {
        const sizeMap: Record<string, string> = {
          sm: 'sm', small: 'sm', md: 'md', medium: 'md', lg: 'lg', large: 'lg',
        };
        return { properties: { size: sizeMap[match[1].toLowerCase()] || 'md' } };
      }
    );

    // Label: "label Power Switch", "set label to Main Light"
    this.commandPatterns.set(
      /(?:set )?label (?:to )?["']?(.+?)["']?\s*$/i,
      (match) => ({ properties: { label: match[1].trim() } })
    );

    // On/Off labels
    this.commandPatterns.set(
      /(?:set )?on label (?:to )?["']?(.+?)["']?\s*$/i,
      (match) => ({ properties: { onLabel: match[1].trim() } })
    );

    this.commandPatterns.set(
      /(?:set )?off label (?:to )?["']?(.+?)["']?\s*$/i,
      (match) => ({ properties: { offLabel: match[1].trim() } })
    );
  }

  canHandle(command: string, component: Component): boolean {
    return component.type === ComponentType.TOGGLE_SWITCH && this.hasToggleKeywords(command);
  }

  private hasToggleKeywords(command: string): boolean {
    const keywords = [
      "toggle", "switch", "on", "off", "enable", "disable",
      "theme", "color", "size", "label", "activate", "deactivate",
      "ios", "material", "neon", "minimal", "pill",
      "small", "medium", "large", "border",
    ];
    const lowerCommand = command.toLowerCase();
    return keywords.some(keyword => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {} };

    for (const part of commandParts) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const patternMatch = this.matchPatterns(trimmed, component);
      if (patternMatch?.properties) {
        changes.properties = { ...changes.properties, ...patternMatch.properties };
      }
    }

    if (!changes.properties || Object.keys(changes.properties).length === 0) {
      return {
        success: false,
        error: "Could not understand toggle switch command",
        suggestions: await this.getExampleCommands(ComponentType.TOGGLE_SWITCH),
      };
    }

    const validation = this.validateChanges(changes, component);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    return {
      success: true,
      command: {
        type: this.determineCommandType(changes),
        changes,
        confidence: this.calculateConfidence(command, changes),
        description: this.generateDescription(changes, component),
      },
    };
  }

  async getExampleCommands(_componentType: ComponentType): Promise<string[]> {
    return [
      "Turn on",
      "Turn off",
      "Toggle",
      "Theme material",
      "Set on color to green",
      "Set off color to gray",
      "Size large",
      "Label Power Switch",
      "Make it neon",
      "Set thumb color to white",
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];
    const props = (component.properties || {}) as Record<string, unknown>;
    const isOn = (props.isOn as boolean) ?? true;
    const currentTheme = (props.theme as string) ?? 'ios';

    suggestions.push(isOn ? "Turn off" : "Turn on");
    suggestions.push("Toggle");

    // Suggest other themes
    const otherThemes = VALID_THEMES.filter(t => t !== currentTheme);
    if (otherThemes.length > 0) {
      suggestions.push(`Theme ${otherThemes[0]}`);
    }

    suggestions.push("Set on color to blue");
    suggestions.push("Size large");
    suggestions.push("Label My Switch");

    return suggestions.slice(0, 6);
  }
}

export const toggleSwitchCommandHandler = new ToggleSwitchCommandHandler();
